/**
 * Reserved client mark slot — monogram now, optional logo URL later.
 * Always renders a fixed square so HUD / gate / switcher rows share one
 * silhouette even when a client has no artwork yet.
 */

import type { CSSProperties } from "react";

/** 1–2 letter mark from a display name (e.g. "Acme Corp" → "AC"). */
export function clientMonogram(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0]?.[0] ?? "";
    const b = parts[1]?.[0] ?? "";
    return `${a}${b}`.toUpperCase();
  }
  const single = parts[0] ?? name;
  return single.slice(0, 2).toUpperCase() || "?";
}

type MarkSize = "sm" | "md" | "lg";

const SIZE_PX: Record<MarkSize, number> = {
  sm: 28,
  md: 36,
  lg: 44,
};

interface ClientMarkProps {
  name: string;
  /** Future registry field — when present, replaces the monogram. */
  logoUrl?: string | null;
  size?: MarkSize;
  className?: string;
  style?: CSSProperties;
}

export function ClientMark({
  name,
  logoUrl,
  size = "md",
  className,
  style,
}: ClientMarkProps) {
  const px = SIZE_PX[size];
  const monogram = clientMonogram(name);

  return (
    <span
      aria-hidden
      className={className}
      style={{
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: px,
        height: px,
        borderRadius: 8,
        border: "1px solid rgba(var(--accent-true-blue-rgb), 0.45)",
        background: "rgba(var(--accent-true-blue-rgb), 0.14)",
        color: "var(--accent-true-blue)",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: size === "lg" ? "var(--fs-base)" : "var(--fs-micro)",
        fontWeight: 700,
        letterSpacing: "0.06em",
        overflow: "hidden",
        ...style,
      }}
    >
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          width={px}
          height={px}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        monogram
      )}
    </span>
  );
}
